import css from "./AICopilotPanel.sass?inline"
import {DefaultObservableValue, Lifecycle, Option, Terminator, UUID} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {createElement, DomElement, Group, replaceChildren} from "@opendaw/lib-jsx"
import {RadioGroup} from "@/ui/components/RadioGroup.tsx"
import {Html} from "@opendaw/lib-dom"
import {Promises} from "@opendaw/lib-runtime"
import {AICopilotScope} from "./AICopilotScope"
import {
    generateSong, separateStems, generateSoundAI,
    fetchAudioAsArrayBuffer, MUSIC_MODELS, ChatMessage, MidiTrackData,
    agentGenerateAndTranscribe, chatAboutMusicGPT5
} from "./AIService"
import {LIBRARY_SAMPLES, LIBRARY_CATEGORIES, LibrarySample} from "./SoundLibrary"
import {AudioContentFactory, Project, ProjectProfile, ProjectMeta} from "@opendaw/studio-core"
import {InstrumentFactories} from "@opendaw/studio-adapters"
import {AudioFileBox} from "@opendaw/studio-boxes"
import {ClassicWaveform, PPQN} from "@opendaw/lib-dsp"
import {VoicingMode} from "@opendaw/studio-enums"
import {DefaultPlayfieldAttachment} from "@/ui/defaults/DefaultPlayfieldAttachment"
import {totalRemaining, useCredit, trackAnalyticsApiCall} from "@/ui/credits/CreditStore"
import {showCheckoutModal} from "@/ui/credits/CheckoutModal"

const className = Html.adoptStyleSheet(css, "AICopilotPanel")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

export const AICopilotPanel = ({lifecycle, service}: Construct) => {
    const scope = new DefaultObservableValue(AICopilotScope.TextToSong)
    const placeholder: DomElement = <Group/>
    const contentLifecycle = lifecycle.own(new Terminator())
    lifecycle.own(scope.catchupAndSubscribe(owner => {
        contentLifecycle.terminate()
        replaceChildren(placeholder, (() => {
            switch (owner.getValue()) {
                case AICopilotScope.TextToSong:
                    return <TextToSongTab lifecycle={contentLifecycle} service={service}/>
                case AICopilotScope.TextToMIDI:
                    return <TextToMIDITab lifecycle={contentLifecycle} service={service}/>
                case AICopilotScope.SoundAI:
                    return <SoundAITab lifecycle={contentLifecycle} service={service}/>
                case AICopilotScope.Library:
                    return <LibraryTab lifecycle={contentLifecycle} service={service}/>
                default:
                    return <span>Unknown</span>
            }
        })())
    }))
    let collapsed = false
    let savedPanelStyles: {minWidth: string, maxWidth: string, flexGrow: string, flexShrink: string, flexBasis: string} | null = null
    const body: HTMLDivElement = <div className="ai-body"/>
    const toggleBtn: HTMLButtonElement = <button className="ai-collapse-btn">&#9664;</button>
    const collapsedLabel: HTMLDivElement = <div className="ai-collapsed-label" onclick={() => toggle()}>AI COPILOT</div>

    const tabsEl = <RadioGroup lifecycle={lifecycle} elements={[
        {value: AICopilotScope.TextToSong, element: <span>Text to Song</span>},
        {value: AICopilotScope.TextToMIDI, element: <span>Text to MIDI</span>},
        {value: AICopilotScope.SoundAI, element: <span>Sound AI</span>},
        {value: AICopilotScope.Library, element: <span>Library</span>}
    ]} model={scope} style={{fontSize: "11px", columnGap: "4px", padding: "6px 8px"}}
                className="ai-tabs"/>

    body.appendChild(tabsEl as unknown as Node)
    body.appendChild(placeholder as unknown as Node)

    const root: HTMLDivElement = <div className={className}/>

    const toggle = () => {
        collapsed = !collapsed
        root.classList.toggle("collapsed", collapsed)
        toggleBtn.innerHTML = collapsed ? "&#9654;" : "&#9664;"
        // Override the parent PanelPlaceholder sizing
        const panelEl = root.closest("[data-panel-type]") as HTMLElement | null
        if (panelEl) {
            if (collapsed) {
                // Save the current styles before overriding
                savedPanelStyles = {
                    minWidth: panelEl.style.minWidth,
                    maxWidth: panelEl.style.maxWidth,
                    flexGrow: panelEl.style.flexGrow,
                    flexShrink: panelEl.style.flexShrink,
                    flexBasis: panelEl.style.flexBasis
                }
                panelEl.style.minWidth = "32px"
                panelEl.style.maxWidth = "32px"
                panelEl.style.flexGrow = "0"
                panelEl.style.flexShrink = "0"
                panelEl.style.flexBasis = "32px"
            } else if (savedPanelStyles) {
                // Restore the original styles
                panelEl.style.minWidth = savedPanelStyles.minWidth
                panelEl.style.maxWidth = savedPanelStyles.maxWidth
                panelEl.style.flexGrow = savedPanelStyles.flexGrow
                panelEl.style.flexShrink = savedPanelStyles.flexShrink
                panelEl.style.flexBasis = savedPanelStyles.flexBasis
                savedPanelStyles = null
            }
        }
    }
    toggleBtn.onclick = toggle

    const header: HTMLDivElement = (
        <div className="ai-header" onclick={(e: MouseEvent) => { if (e.target !== toggleBtn) toggle() }}>
            <span>AI COPILOT</span>
            {toggleBtn}
        </div>
    )

    root.appendChild(header as unknown as Node)
    root.appendChild(body as unknown as Node)
    root.appendChild(collapsedLabel as unknown as Node)

    return root
}

// ──────────────────────── helpers ────────────────────────

function ensureProject(service: StudioService): Project {
    if (!service.hasProfile) {
        const project = Project.new(service)
        ;(service as any).projectProfileService?.setValue?.(
            Option.wrap(new ProjectProfile(UUID.generate(), project, ProjectMeta.init("Untitled"), Option.None))
        )
        // fallback: try the public setter
        if (!service.hasProfile) {
            ;(service as any)["_projectProfileService"]?.setValue?.(
                Option.wrap(new ProjectProfile(UUID.generate(), project, ProjectMeta.init("Untitled"), Option.None))
            )
        }
    }
    return service.project
}

async function importAudioToTrack(service: StudioService, name: string, arrayBuffer: ArrayBuffer): Promise<void> {
    const {status, value: sample, error} = await Promises.tryCatch(
        service.sampleService.importFile({name, arrayBuffer})
    )
    if (status === "rejected") {
        console.warn(`Failed to import '${name}'`, error)
        return
    }
    const uuid = UUID.parse(sample.uuid)
    await Promises.tryCatch(service.sampleManager.getAudioData(uuid))
    const project = ensureProject(service)
    project.editing.modify(() => {
        const {trackBox, instrumentBox} = project.api.createInstrument(InstrumentFactories.Tape)
        instrumentBox.label.setValue(name)
        const audioFileBox = project.boxGraph.findBox<AudioFileBox>(uuid)
            .unwrapOrElse(() => AudioFileBox.create(project.boxGraph, uuid, box => {
                box.fileName.setValue(name)
                box.startInSeconds.setValue(0)
                box.endInSeconds.setValue(sample.duration)
            }))
        AudioContentFactory.createNotStretchedRegion({
            boxGraph: project.boxGraph, sample, audioFileBox, position: 0, targetTrack: trackBox
        })
    })
}

// GM drum note → Playfield slot (909 kit: 60=kick, 61=snare, 62=low tom, 63=mid tom,
// 64=high tom, 65=rimshot, 66=clap, 67=closed hat, 68=open hat, 69=crash, 70=ride)
const GM_DRUM_TO_PLAYFIELD: Record<number, number> = {
    35: 60, 36: 60,           // Bass Drum → 909 Bassdrum
    38: 61, 40: 61,           // Snare → 909 Snare
    41: 62, 43: 62,           // Low Tom
    45: 63, 47: 63,           // Mid Tom
    48: 64, 50: 64,           // High Tom
    37: 65,                   // Side Stick → Rimshot
    39: 66,                   // Clap
    42: 67, 44: 67,           // Closed/Pedal HH → Closed Hat
    46: 68,                   // Open HH → Open Hat
    49: 69, 57: 69,           // Crash
    51: 70, 59: 70,           // Ride
}

// ── Instrument routing ──

type InstrumentChoice =
    | "drums"
    | "bass-synth"
    | "sub-bass"
    | "lead-synth"
    | "pad-synth"
    | "pluck-synth"
    | "organ-synth"
    | "synth-brass"
    | "soundfont"
    // Rap / Hip-Hop
    | "808-bass"
    | "trap-lead"
    | "trap-bell"
    | "rap-keys"
    | "dark-pad"
    // EDM
    | "supersaw-lead"
    | "supersaw-pad"
    | "acid-bass"
    | "wobble-bass"
    | "edm-pluck"
    | "arp-synth"
    | "sidechain-pad"

function chooseInstrument(track: MidiTrackData): {choice: InstrumentChoice, presetIndex?: number} {
    const ch = track.channel
    const p = track.program ?? -1
    const n = track.name.toLowerCase()

    // Channel 9 is always drums in GM
    if (ch === 9) return {choice: "drums"}

    // Name-based drum detection (for stems that lack channel info)
    if (n.includes("drum") || n.includes("percus") || n.includes("kick") || n.includes("snare") || n.includes("hat")) return {choice: "drums"}

    // GM program-based routing
    if (p >= 0) {
        // Bass family (32-39)
        if (p >= 32 && p <= 35) return {choice: "bass-synth"}
        if (p >= 36 && p <= 37) return {choice: "bass-synth"}  // slap bass
        if (p >= 38 && p <= 39) return {choice: "sub-bass"}     // synth bass

        // Synth leads (80-87)
        if (p >= 80 && p <= 87) return {choice: "lead-synth"}

        // Synth pads (88-95)
        if (p >= 88 && p <= 95) return {choice: "pad-synth"}

        // Organs (16-23)
        if (p >= 16 && p <= 23) return {choice: "organ-synth"}

        // Synth brass (62-63)
        if (p === 62 || p === 63) return {choice: "synth-brass"}

        // Piano family (0-7) → soundfont with preset index
        if (p >= 0 && p <= 7) return {choice: "soundfont", presetIndex: p}

        // Guitar family (24-31) → soundfont
        if (p >= 24 && p <= 31) return {choice: "soundfont", presetIndex: p}

        // Strings (40-51) → soundfont
        if (p >= 40 && p <= 51) return {choice: "soundfont", presetIndex: p}

        // Brass (56-61) → soundfont
        if (p >= 56 && p <= 61) return {choice: "soundfont", presetIndex: p}

        // Woodwinds (64-79) → soundfont
        if (p >= 64 && p <= 79) return {choice: "soundfont", presetIndex: p}

        // Chromatic percussion (8-15) → soundfont
        if (p >= 8 && p <= 15) return {choice: "soundfont", presetIndex: p}
    }

    // ── Rap / Hip-Hop name-based routing ──
    if (n.includes("808")) return {choice: "808-bass"}
    if (n.includes("trap") && (n.includes("lead") || n.includes("melody"))) return {choice: "trap-lead"}
    if (n.includes("trap") && n.includes("bell")) return {choice: "trap-bell"}
    if (n.includes("trap") && n.includes("pad")) return {choice: "dark-pad"}
    if (n.includes("dark") && n.includes("pad")) return {choice: "dark-pad"}
    if (n.includes("rap") && (n.includes("key") || n.includes("piano"))) return {choice: "rap-keys"}

    // ── EDM name-based routing ──
    if (n.includes("supersaw") && n.includes("lead")) return {choice: "supersaw-lead"}
    if (n.includes("supersaw") && n.includes("pad")) return {choice: "supersaw-pad"}
    if (n.includes("supersaw")) return {choice: "supersaw-lead"}
    if (n.includes("acid")) return {choice: "acid-bass"}
    if (n.includes("wobble")) return {choice: "wobble-bass"}
    if (n.includes("edm") && n.includes("pluck")) return {choice: "edm-pluck"}
    if (n.includes("sidechain")) return {choice: "sidechain-pad"}
    if (n.includes("arp")) return {choice: "arp-synth"}
    if (n.includes("trance")) return {choice: "supersaw-lead"}
    if (n.includes("dubstep")) return {choice: "wobble-bass"}
    if (n.includes("house") && n.includes("bass")) return {choice: "acid-bass"}
    if (n.includes("reese")) return {choice: "wobble-bass"}

    // ── General name-based fallbacks ──
    if (n.includes("vocal") || n.includes("voice") || n.includes("sing")) return {choice: "lead-synth"}
    if (n.includes("bass")) return {choice: "bass-synth"}
    if (n.includes("sub")) return {choice: "sub-bass"}
    if (n.includes("lead") || n.includes("melody")) return {choice: "lead-synth"}
    if (n.includes("pad") || n.includes("string") || n.includes("ambient")) return {choice: "pad-synth"}
    if (n.includes("pluck")) return {choice: "pluck-synth"}
    if (n.includes("organ")) return {choice: "organ-synth"}
    if (n.includes("brass") || n.includes("horn")) return {choice: "synth-brass"}
    if (n.includes("bell") || n.includes("chime")) return {choice: "trap-bell"}
    if (n.includes("piano") || n.includes("key")) return {choice: "soundfont", presetIndex: 0}
    if (n.includes("guitar")) return {choice: "soundfont", presetIndex: 24}
    if (n.includes("violin") || n.includes("cello") || n.includes("viola")) return {choice: "soundfont", presetIndex: 48}
    if (n.includes("trumpet")) return {choice: "soundfont", presetIndex: 56}
    if (n.includes("sax")) return {choice: "soundfont", presetIndex: 65}
    if (n.includes("flute")) return {choice: "soundfont", presetIndex: 73}

    // Default: soundfont piano
    return {choice: "soundfont", presetIndex: 0}
}

// ── Vaporisateur preset configurations ──

type VaporPreset = {
    cutoff: number
    resonance: number
    attack: number
    decay: number
    sustain: number
    release: number
    filterEnvelope: number
    voicingMode: number  // VoicingMode enum
    glideTime: number
    unisonCount: number
    unisonDetune: number
    filterOrder: number
    osc1Waveform: number
    osc1Volume: number
    osc2Waveform: number
    osc2Volume: number
    tune: number
}

const VAPOR_PRESETS: Record<string, VaporPreset> = {
    "bass-synth": {
        cutoff: 1200, resonance: 0.3, attack: 0.005, decay: 0.200, sustain: 0.7, release: 0.15,
        filterEnvelope: 0.3, voicingMode: VoicingMode.Monophonic, glideTime: 0.05,
        unisonCount: 1, unisonDetune: 30, filterOrder: 2,
        osc1Waveform: ClassicWaveform.square, osc1Volume: -3,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: -9, tune: -12
    },
    "sub-bass": {
        cutoff: 800, resonance: 0.1, attack: 0.005, decay: 0.4, sustain: 0.9, release: 0.2,
        filterEnvelope: 0.1, voicingMode: VoicingMode.Monophonic, glideTime: 0.1,
        unisonCount: 1, unisonDetune: 30, filterOrder: 3,
        osc1Waveform: ClassicWaveform.sine, osc1Volume: -3,
        osc2Waveform: ClassicWaveform.sine, osc2Volume: Number.NEGATIVE_INFINITY, tune: -12
    },
    "lead-synth": {
        cutoff: 8000, resonance: 0.2, attack: 0.005, decay: 0.15, sustain: 0.8, release: 0.25,
        filterEnvelope: 0.2, voicingMode: VoicingMode.Monophonic, glideTime: 0.08,
        unisonCount: 3, unisonDetune: 15, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -12, tune: 0
    },
    "pad-synth": {
        cutoff: 4000, resonance: 0.1, attack: 0.8, decay: 1.0, sustain: 0.7, release: 1.5,
        filterEnvelope: 0.15, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 5, unisonDetune: 25, filterOrder: 1,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -9,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -12, tune: 0
    },
    "pluck-synth": {
        cutoff: 6000, resonance: 0.4, attack: 0.001, decay: 0.3, sustain: 0.1, release: 0.2,
        filterEnvelope: 0.6, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 1, unisonDetune: 30, filterOrder: 2,
        osc1Waveform: ClassicWaveform.square, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: -12, tune: 0
    },
    "organ-synth": {
        cutoff: 12000, resonance: 0.05, attack: 0.001, decay: 0.01, sustain: 1.0, release: 0.08,
        filterEnvelope: 0, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 1, unisonDetune: 30, filterOrder: 1,
        osc1Waveform: ClassicWaveform.square, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -9, tune: 0
    },
    "synth-brass": {
        cutoff: 3000, resonance: 0.3, attack: 0.03, decay: 0.25, sustain: 0.6, release: 0.3,
        filterEnvelope: 0.5, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 3, unisonDetune: 20, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: -9, tune: 0
    },

    // ── Rap / Hip-Hop presets ──

    // 808 bass: deep sine sub with long decay, slight saturation via resonance, mono glide
    "808-bass": {
        cutoff: 600, resonance: 0.25, attack: 0.001, decay: 1.5, sustain: 0.3, release: 0.8,
        filterEnvelope: 0.4, voicingMode: VoicingMode.Monophonic, glideTime: 0.12,
        unisonCount: 1, unisonDetune: 0, filterOrder: 3,
        osc1Waveform: ClassicWaveform.sine, osc1Volume: -2,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -18, tune: -12
    },
    // Trap lead: bright detuned saw, aggressive, mono with glide
    "trap-lead": {
        cutoff: 10000, resonance: 0.15, attack: 0.002, decay: 0.2, sustain: 0.85, release: 0.15,
        filterEnvelope: 0.1, voicingMode: VoicingMode.Monophonic, glideTime: 0.06,
        unisonCount: 3, unisonDetune: 12, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -9, tune: 0
    },
    // Trap bell: high-pitched plucky square, short decay, bell-like
    "trap-bell": {
        cutoff: 14000, resonance: 0.1, attack: 0.001, decay: 0.4, sustain: 0.05, release: 0.5,
        filterEnvelope: 0.7, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 1, unisonDetune: 0, filterOrder: 1,
        osc1Waveform: ClassicWaveform.square, osc1Volume: -8,
        osc2Waveform: ClassicWaveform.sine, osc2Volume: -12, tune: 12
    },
    // Rap keys: dark electric piano-ish, muted saw + square, medium attack
    "rap-keys": {
        cutoff: 3500, resonance: 0.15, attack: 0.005, decay: 0.6, sustain: 0.4, release: 0.3,
        filterEnvelope: 0.25, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 2, unisonDetune: 8, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -9,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -12, tune: 0
    },
    // Dark pad: slow evolving dark texture, low cutoff, lots of unison
    "dark-pad": {
        cutoff: 2000, resonance: 0.2, attack: 1.2, decay: 1.5, sustain: 0.6, release: 2.0,
        filterEnvelope: 0.1, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 5, unisonDetune: 30, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -9,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -12, tune: -12
    },

    // ── EDM presets ──

    // Supersaw lead: massive detuned unison saws, bright, for trance/EDM drops
    "supersaw-lead": {
        cutoff: 12000, resonance: 0.1, attack: 0.002, decay: 0.15, sustain: 0.9, release: 0.2,
        filterEnvelope: 0.05, voicingMode: VoicingMode.Monophonic, glideTime: 0.04,
        unisonCount: 7, unisonDetune: 20, filterOrder: 1,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: -6, tune: 0
    },
    // Supersaw pad: wide detuned saws, slow attack for EDM chords/breakdowns
    "supersaw-pad": {
        cutoff: 8000, resonance: 0.08, attack: 0.5, decay: 0.8, sustain: 0.7, release: 1.2,
        filterEnvelope: 0.1, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 7, unisonDetune: 25, filterOrder: 1,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: -9, tune: 0
    },
    // Acid bass: squelchy resonant saw, TB-303 style, mono with glide
    "acid-bass": {
        cutoff: 800, resonance: 0.7, attack: 0.001, decay: 0.2, sustain: 0.3, release: 0.1,
        filterEnvelope: 0.8, voicingMode: VoicingMode.Monophonic, glideTime: 0.06,
        unisonCount: 1, unisonDetune: 0, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -3,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: Number.NEGATIVE_INFINITY, tune: 0
    },
    // Wobble bass: thick detuned saws, heavy low end, dubstep/bass music
    "wobble-bass": {
        cutoff: 1500, resonance: 0.5, attack: 0.001, decay: 0.3, sustain: 0.6, release: 0.15,
        filterEnvelope: 0.6, voicingMode: VoicingMode.Monophonic, glideTime: 0.03,
        unisonCount: 3, unisonDetune: 15, filterOrder: 3,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -3,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -6, tune: -12
    },
    // EDM pluck: short snappy attack, quick decay, bright — for future bass / tropical
    "edm-pluck": {
        cutoff: 10000, resonance: 0.25, attack: 0.001, decay: 0.15, sustain: 0.05, release: 0.25,
        filterEnvelope: 0.7, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 3, unisonDetune: 12, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -9, tune: 0
    },
    // Arp synth: crisp short notes, slight detune, good for arpeggiated sequences
    "arp-synth": {
        cutoff: 8000, resonance: 0.2, attack: 0.001, decay: 0.2, sustain: 0.15, release: 0.15,
        filterEnvelope: 0.5, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 2, unisonDetune: 10, filterOrder: 2,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -6,
        osc2Waveform: ClassicWaveform.square, osc2Volume: -12, tune: 12
    },
    // Sidechain pad: pumping EDM pad, slow attack/release for that "breathing" feel
    "sidechain-pad": {
        cutoff: 5000, resonance: 0.1, attack: 0.3, decay: 0.5, sustain: 0.8, release: 0.6,
        filterEnvelope: 0.15, voicingMode: VoicingMode.Polyphonic, glideTime: 0,
        unisonCount: 5, unisonDetune: 20, filterOrder: 1,
        osc1Waveform: ClassicWaveform.saw, osc1Volume: -9,
        osc2Waveform: ClassicWaveform.saw, osc2Volume: -12, tune: 0
    }
}

function applyVaporPreset(instrumentBox: any, preset: VaporPreset): void {
    instrumentBox.tune.setValue(preset.tune)
    instrumentBox.cutoff.setValue(preset.cutoff)
    instrumentBox.resonance.setValue(preset.resonance)
    instrumentBox.attack.setValue(preset.attack)
    instrumentBox.decay.setValue(preset.decay)
    instrumentBox.sustain.setValue(preset.sustain)
    instrumentBox.release.setValue(preset.release)
    instrumentBox.filterEnvelope.setValue(preset.filterEnvelope)
    instrumentBox.voicingMode.setValue(preset.voicingMode)
    instrumentBox.glideTime.setValue(preset.glideTime)
    instrumentBox.unisonCount.setValue(preset.unisonCount)
    instrumentBox.unisonDetune.setValue(preset.unisonDetune)
    instrumentBox.filterOrder.setValue(preset.filterOrder)
    instrumentBox.oscillators.fields()[0].waveform.setValue(preset.osc1Waveform)
    instrumentBox.oscillators.fields()[0].volume.setValue(preset.osc1Volume)
    instrumentBox.oscillators.fields()[1].waveform.setValue(preset.osc2Waveform)
    instrumentBox.oscillators.fields()[1].volume.setValue(preset.osc2Volume)
}

// ── Prompt-aware instrument inference ──
// Maps user prompt keywords to specific instrument names per demucs stem,
// so that chooseInstrument() routes tracks to the correct synth preset.

type StemInstrumentMap = Record<string, string>  // stem name → instrument label

function inferStemInstruments(prompt: string): StemInstrumentMap {
    const p = prompt.toLowerCase()
    const map: StemInstrumentMap = {}

    // ── Drums stem ──
    // Always drums, but label can hint at style
    if (p.includes("trap") || p.includes("drill")) {
        map["drums"] = "Trap Drums"
    } else if (p.includes("edm") || p.includes("house") || p.includes("techno")) {
        map["drums"] = "EDM Drums"
    } else {
        map["drums"] = "Drums"
    }

    // ── Bass stem ──
    if (p.includes("808")) {
        map["bass"] = "808 Bass"
    } else if (p.includes("acid")) {
        map["bass"] = "Acid Bass"
    } else if (p.includes("wobble") || p.includes("dubstep")) {
        map["bass"] = "Wobble Bass"
    } else if (p.includes("reese")) {
        map["bass"] = "Reese Bass"
    } else if (p.includes("sub")) {
        map["bass"] = "Sub Bass"
    } else if (p.includes("house") && p.includes("bass")) {
        map["bass"] = "House Bass"
    } else if (p.includes("drill")) {
        map["bass"] = "808 Bass"  // drill = 808
    } else if (p.includes("trap")) {
        map["bass"] = "808 Bass"  // trap = 808
    } else if (p.includes("phonk")) {
        map["bass"] = "808 Bass"
    } else if (p.includes("edm") || p.includes("trance")) {
        map["bass"] = "Acid Bass"
    } else {
        map["bass"] = "Bass"
    }

    // ── Vocals stem ──
    if (p.includes("vocal") || p.includes("voice") || p.includes("sing")) {
        map["vocals"] = "Vocal Lead"
    } else {
        map["vocals"] = "Vocal Synth Lead"  // transcribed vocal melody → synth lead
    }

    // ── Other stem (most important — this is where synths/keys/bells/pads live) ──
    // Try to pick the most specific match from the prompt
    const otherInstruments: string[] = []

    if (p.includes("supersaw") && p.includes("lead")) otherInstruments.push("Supersaw Lead")
    else if (p.includes("supersaw") && p.includes("pad")) otherInstruments.push("Supersaw Pad")
    else if (p.includes("supersaw")) otherInstruments.push("Supersaw Lead")

    if (p.includes("trap") && p.includes("bell")) otherInstruments.push("Trap Bell")
    if (p.includes("trap") && p.includes("lead")) otherInstruments.push("Trap Lead")
    if (p.includes("dark") && p.includes("pad")) otherInstruments.push("Dark Pad")
    if (p.includes("rap") && (p.includes("key") || p.includes("piano"))) otherInstruments.push("Rap Keys")
    if (p.includes("pluck")) otherInstruments.push("EDM Pluck")
    if (p.includes("arp")) otherInstruments.push("Arp Synth")
    if (p.includes("sidechain") && p.includes("pad")) otherInstruments.push("Sidechain Pad")
    if (p.includes("organ")) otherInstruments.push("Organ")
    if (p.includes("bell") && !p.includes("trap")) otherInstruments.push("Trap Bell")
    if (p.includes("brass") || p.includes("horn")) otherInstruments.push("Synth Brass")

    // Genre-based defaults if nothing specific was found
    if (otherInstruments.length === 0) {
        if (p.includes("trap") || p.includes("drill")) {
            otherInstruments.push("Trap Bell", "Dark Pad")
        } else if (p.includes("lo-fi") || p.includes("lofi") || p.includes("hip hop") || p.includes("hip-hop")) {
            otherInstruments.push("Rap Keys", "Dark Pad")
        } else if (p.includes("edm") || p.includes("house") || p.includes("trance")) {
            otherInstruments.push("Supersaw Lead", "Supersaw Pad")
        } else if (p.includes("dubstep")) {
            otherInstruments.push("Wobble Bass", "Supersaw Lead")
        } else if (p.includes("future bass")) {
            otherInstruments.push("EDM Pluck", "Supersaw Pad")
        } else if (p.includes("phonk")) {
            otherInstruments.push("Trap Lead", "Dark Pad")
        } else {
            otherInstruments.push("Pad", "Lead")
        }
    }

    // Use the first match as the primary "other" instrument label
    map["other"] = otherInstruments[0]
    // Store extras for multi-track "other" stems
    if (otherInstruments.length > 1) {
        map["other_alt"] = otherInstruments[1]
    }

    return map
}

function addMidiTracksToProject(service: StudioService, tracks: MidiTrackData[], bpm: number): void {
    const project = ensureProject(service)
    project.editing.modify(() => {
        project.api.setBpm(bpm)
        for (const track of tracks) {
            const {choice, presetIndex} = chooseInstrument(track)
            let trackBox: any
            let instrumentBox: any
            let isDrums = false

            if (choice === "drums") {
                isDrums = true
                const result = project.api.createInstrument(InstrumentFactories.Playfield, {
                    attachment: DefaultPlayfieldAttachment
                })
                trackBox = result.trackBox
                instrumentBox = result.instrumentBox
            } else if (choice === "soundfont") {
                const result = project.api.createInstrument(InstrumentFactories.Soundfont)
                trackBox = result.trackBox
                instrumentBox = result.instrumentBox
                // Set the GM preset index to select the right instrument sound
                if (presetIndex !== undefined && presetIndex > 0) {
                    instrumentBox.presetIndex.setValue(presetIndex)
                }
            } else {
                // All synth variants use Vaporisateur with different presets
                const result = project.api.createInstrument(InstrumentFactories.Vaporisateur)
                trackBox = result.trackBox
                instrumentBox = result.instrumentBox
                const preset = VAPOR_PRESETS[choice]
                if (preset) applyVaporPreset(instrumentBox, preset)
            }

            instrumentBox.label.setValue(track.name)
            if (track.notes.length === 0) continue

            // For drums, remap GM note numbers to Playfield slots
            const notes = isDrums
                ? track.notes.map(n => ({
                    ...n,
                    pitch: GM_DRUM_TO_PLAYFIELD[n.pitch] ?? 60
                }))
                : track.notes

            const maxBeat = Math.max(...notes.map(n => n.position + n.duration))
            const durationPulses = Math.ceil(maxBeat / 4) * PPQN.Bar
            const region = project.api.createNoteRegion({
                trackBox,
                position: 0,
                duration: Math.max(durationPulses, PPQN.Bar)
            })
            for (const note of notes) {
                project.api.createNoteEvent({
                    owner: region,
                    position: Math.round(note.position * PPQN.Quarter),
                    duration: Math.max(Math.round(note.duration * PPQN.Quarter), 1),
                    pitch: note.pitch,
                    velocity: note.velocity ?? 0.8
                })
            }
        }
    })
}

// ──────────────────────── Text to Song ────────────────────────

const TextToSongTab = ({service}: Construct) => {
    let selectedModel = MUSIC_MODELS[0].id
    let prompt = ""
    let duration = 30
    let bpm = 120
    let splitStems = true
    let includeLyrics = false
    let generating = false

    const statusEl: HTMLDivElement = <div style={{display: "none"}}/>
    const textarea: HTMLTextAreaElement = (
        <textarea className="ai-textarea"
                  placeholder="E.g., chill lo-fi hip hop with vinyl crackle and jazzy piano..."
                  oninput={(e: Event) => prompt = (e.target as HTMLTextAreaElement).value}/>
    )
    const durationLabel: HTMLSpanElement = <span>{duration}s</span>
    const bpmInput: HTMLSpanElement = <span>{bpm}</span>
    const generateBtn: HTMLButtonElement = <button className="ai-generate-btn">Generate {duration}s</button>

    const slider: HTMLInputElement = (
        <input type="range" className="ai-slider" min="5" max="300" value={String(duration)}
               oninput={(e: Event) => {
                   duration = parseInt((e.target as HTMLInputElement).value)
                   durationLabel.textContent = duration >= 60 ? `${Math.floor(duration / 60)}m ${duration % 60}s` : `${duration}s`
                   generateBtn.textContent = `Generate ${durationLabel.textContent}`
               }}/>
    )

    const setDuration = (val: number) => {
        duration = val
        slider.value = String(val)
        durationLabel.textContent = val >= 60 ? `${Math.floor(val / 60)}m ${val % 60}s` : `${val}s`
        generateBtn.textContent = `Generate ${durationLabel.textContent}`
        document.querySelectorAll(".ai-preset-btn[data-dur]").forEach(b => {
            b.classList.toggle("active", parseInt(b.getAttribute("data-dur") ?? "0") === val)
        })
    }

    const setBpm = (val: number) => {
        bpm = val
        bpmInput.textContent = String(val)
        document.querySelectorAll(".ai-preset-btn[data-bpm]").forEach(b => {
            b.classList.toggle("active", parseInt(b.getAttribute("data-bpm") ?? "0") === val)
        })
    }

    const showStatus = (msg: string, type: "" | "success" | "error" = "") => {
        statusEl.style.display = "flex"
        statusEl.className = `ai-status ${type}`
        statusEl.innerHTML = type === "" ? `<div class="ai-spinner"></div>${msg}` : msg
    }

    const doGenerate = async () => {
        if (generating || !prompt.trim()) return
        if (totalRemaining() <= 0) { showCheckoutModal(); return }
        useCredit()
        generating = true
        generateBtn.disabled = true
        try {
              showStatus("Generating song...")
                trackAnalyticsApiCall("/api/replicate/predictions", `replicate-musicgen`)
                const audioUrl = await generateSong(selectedModel, prompt, duration, bpm, !includeLyrics)
            showStatus("Downloading audio...")
            const audioBuffer = await fetchAudioAsArrayBuffer(audioUrl)

            if (splitStems) {
                showStatus("Separating stems (vocals, drums, bass, other)...")
                const stems = await separateStems(audioUrl)
                for (const [stemName, stemUrl] of Object.entries(stems)) {
                    showStatus(`Importing ${stemName} stem...`)
                    const stemBuffer = await fetchAudioAsArrayBuffer(stemUrl)
                    await importAudioToTrack(service, `${prompt.slice(0, 20)} - ${stemName}`, stemBuffer)
                }
                showStatus("All stems imported to tracks!", "success")
            } else {
                await importAudioToTrack(service, `${prompt.slice(0, 30)}`, audioBuffer)
                showStatus("Song imported to track!", "success")
            }
        } catch (e: any) {
            showStatus(`Error: ${e.message}`, "error")
        } finally {
            generating = false
            generateBtn.disabled = false
        }
    }

    generateBtn.onclick = doGenerate

    const setPrompt = (text: string) => {
        prompt = text
        textarea.value = text
    }

    return (
        <div className="ai-content">
            <div>
                <h3>Text to Song</h3>
                <p className="ai-subtitle">Describe a song — AI generates it, splits into stems, then analyzes and recommends edits.</p>
            </div>
            <div>
                <div className="ai-label">AI Model</div>
                <div className="ai-select-wrapper">
                    <select onchange={(e: Event) => selectedModel = (e.target as HTMLSelectElement).value}>
                        {MUSIC_MODELS.map(m => <option value={m.id}>{m.name}</option>)}
                    </select>
                </div>
                <div className="ai-model-info">{MUSIC_MODELS[0].description}</div>
            </div>
            <div>
                <div className="ai-label">Describe your song</div>
                {textarea}
            </div>
            <div>
                <div className="ai-control-row">
                    <span className="ai-control-label">Duration</span>
                    <span className="ai-control-value">{durationLabel}</span>
                </div>
                {slider}
                <div style={{display: "flex", justifyContent: "space-between", fontSize: "9px", color: "#666", marginTop: "2px"}}>
                    <span>5s</span><span>5m</span>
                </div>
                <div className="ai-preset-row" style={{marginTop: "6px"}}>
                    {[15, 30, 60, 120, 180].map(d =>
                        <button className={`ai-preset-btn${d === 30 ? " active" : ""}`}
                                data-dur={String(d)}
                                onclick={() => setDuration(d)}>
                            {d >= 60 ? `${d / 60}m` : `${d}s`}
                        </button>
                    )}
                </div>
            </div>
            <div>
                <div className="ai-control-row">
                    <span className="ai-control-label">BPM</span>
                    <span className="ai-control-value">{bpmInput}</span>
                </div>
                <div className="ai-preset-row" style={{marginTop: "6px"}}>
                    {[80, 100, 120, 140].map(b =>
                        <button className={`ai-preset-btn${b === 120 ? " active" : ""}`}
                                data-bpm={String(b)}
                                onclick={() => setBpm(b)}>
                            {b}
                        </button>
                    )}
                    <button className="ai-preset-btn" onclick={() => {
                        if (service.hasProfile) {
                            const projBpm = service.project.timelineBoxAdapter.box.bpm.getValue()
                            setBpm(Math.round(projBpm))
                        }
                    }}>Sync</button>
                </div>
            </div>
            <div className="ai-toggle-row">
                <div className="ai-toggle-text">
                    <h4>Split into stems</h4>
                    <p>Vocals, drums, bass & other as separate tracks</p>
                </div>
                <label className="ai-toggle">
                    <input type="checkbox" checked={true}
                           onchange={(e: Event) => splitStems = (e.target as HTMLInputElement).checked}/>
                    <div className="ai-toggle-slider"/>
                </label>
            </div>
            <div className="ai-toggle-row">
                <div className="ai-toggle-text">
                    <h4>Include lyrics</h4>
                    <p>Allow AI-generated vocals and lyrics in the song</p>
                </div>
                <label className="ai-toggle">
                    <input type="checkbox" checked={false}
                           onchange={(e: Event) => includeLyrics = (e.target as HTMLInputElement).checked}/>
                    <div className="ai-toggle-slider"/>
                </label>
            </div>
            {generateBtn}
            {statusEl}
            <div className="ai-ideas">
                <div className="ai-ideas-label">Ideas</div>
                {["Trap beat with heavy 808 bass and hi-hats", "EDM drop with supersaw lead and acid bass",
                    "Lo-fi hip hop with dark pads and rap keys", "Drill beat with sliding 808 and trap bells",
                    "Future bass with pluck synths and supersaw chords", "Dubstep wobble bass with heavy drops"].map(idea =>
                    <button className="ai-idea-btn" onclick={() => setPrompt(idea)}>{idea}</button>
                )}
            </div>
        </div>
    )
}

// ──────────────────────── Text to MIDI ────────────────────────

const TextToMIDITab = ({service}: Construct) => {
    let mode: "agent" | "chat" = "agent"
    let inputText = ""
    let chatHistory: ChatMessage[] = []
    let allMidiTracks: MidiTrackData[] = []  // accumulated MIDI tracks for chat context
    let processing = false

    const messagesEl: HTMLDivElement = <div className="ai-chat-messages"/>
    const inputEl: HTMLInputElement = (
        <input type="text" placeholder="Describe MIDI to create or edit..."
               onkeydown={(e: KeyboardEvent) => { if (e.key === "Enter") doSend() }}
               oninput={(e: Event) => inputText = (e.target as HTMLInputElement).value}/>
    )

    const agentBtn: HTMLButtonElement = <button className="ai-mode-btn active">Agent</button>
    const chatBtn: HTMLButtonElement = <button className="ai-mode-btn">Chat</button>
    const modeDesc: HTMLSpanElement = <span className="ai-mode-desc">Generate song, split stems, transcribe to MIDI</span>

    agentBtn.onclick = () => {
        mode = "agent"
        agentBtn.classList.add("active")
        chatBtn.classList.remove("active")
        modeDesc.textContent = "Generate song, split stems, transcribe to MIDI"
    }
    chatBtn.onclick = () => {
        mode = "chat"
        chatBtn.classList.add("active")
        agentBtn.classList.remove("active")
        modeDesc.textContent = "Chat about your MIDI tracks (GPT-5)"
    }

    const addMessage = (msg: ChatMessage) => {
        chatHistory.push(msg)
        const el: HTMLDivElement = <div className={`ai-chat-msg ${msg.role}`}>{msg.content}</div>
        if (msg.midiTracks && msg.midiTracks.length > 0) {
            const info: HTMLDivElement = (
                <div className="ai-midi-tracks-info">
                    Added {msg.midiTracks.length} MIDI track{msg.midiTracks.length > 1 ? "s" : ""}: {msg.midiTracks.map(t => t.name).join(", ")}
                </div>
            )
            el.appendChild(info)
        }
        messagesEl.appendChild(el)
        messagesEl.scrollTop = messagesEl.scrollHeight
    }

    const addStatusMessage = (text: string) => {
        const el: HTMLDivElement = <div className="ai-chat-msg assistant ai-status-step"><div className="ai-spinner" style={{display: "inline-block", marginRight: "8px"}}/>{text}</div>
        messagesEl.appendChild(el)
        messagesEl.scrollTop = messagesEl.scrollHeight
        return el
    }

    const updateStatusMessage = (el: HTMLDivElement, text: string) => {
        el.innerHTML = `<div class="ai-spinner" style="display:inline-block;margin-right:8px"></div>${text}`
        messagesEl.scrollTop = messagesEl.scrollHeight
    }

    const showEmpty = () => {
        if (chatHistory.length > 0) return
        const emptyEl: HTMLDivElement = (
            <div className="ai-chat-empty">
                <div className="ai-chat-icon">🎹</div>
                <h4>Text to MIDI</h4>
                <p><strong>Agent mode:</strong> Describe a song — AI generates it with ElevenLabs, splits stems, then transcribes each to MIDI and adds tracks to the timeline.</p>
                <p><strong>Chat mode:</strong> Discuss your MIDI tracks with GPT-5 — get advice on arrangement, theory, and improvements.</p>
                <div className="ai-chat-suggestions">
                    {["Make a trap beat with 808 bass and trap bells",
                        "Create an EDM drop with supersaw lead and wobble bass",
                        "Generate a lo-fi hip hop beat with dark pads and rap keys",
                        "Write a dubstep track with wobble bass and acid synths",
                        "Make a drill beat with 808 slides and dark melody",
                        "Create a future bass track with pluck synths and supersaw pad"].map(s =>
                        <button className="ai-suggestion-btn" onclick={() => {
                            inputText = s
                            inputEl.value = s
                            doSend()
                        }}>{s}</button>
                    )}
                </div>
            </div>
        )
        messagesEl.appendChild(emptyEl)
    }

    const doSend = async () => {
        if (processing || !inputText.trim()) return
        processing = true
        const text = inputText.trim()
        inputText = ""
        inputEl.value = ""

        // Clear empty state
        if (chatHistory.length === 0) messagesEl.innerHTML = ""

        addMessage({role: "user", content: text})

        const bpm = service.hasProfile
            ? Math.round(service.project.timelineBoxAdapter.box.bpm.getValue())
            : 120

        try {
            if (mode === "agent") {
                if (totalRemaining() <= 0) { showCheckoutModal(); processing = false; return }
                useCredit()
                // New pipeline: ElevenLabs -> stem split -> transcribe each to MIDI -> add to timeline
                trackAnalyticsApiCall("/api/replicate/predictions", "replicate-musicgen")
                const statusEl = addStatusMessage("Starting pipeline...")

                const result = await agentGenerateAndTranscribe(text, bpm, (step) => {
                    updateStatusMessage(statusEl, step)
                })

                // Remove the status element
                if (statusEl.parentNode) statusEl.parentNode.removeChild(statusEl)

                // Infer instruments from the user's prompt for each stem
                const stemInstrumentMap = inferStemInstruments(text)

                // Add MIDI tracks to the project with prompt-aware instrument names
                const allNewTracks: MidiTrackData[] = []
                for (const [stemName, stemData] of Object.entries(result.stems)) {
                    if (stemData.tracks.length > 0) {
                        // Use the inferred instrument name instead of generic stem name
                        const inferredName = stemInstrumentMap[stemName] ?? stemName
                        const altName = stemInstrumentMap[`${stemName}_alt`]

                        const prefixedTracks = stemData.tracks.map((t, i) => {
                            // For multi-track stems, alternate between primary and alt instruments
                            const label = (i > 0 && altName) ? altName : inferredName
                            return {
                                ...t,
                                name: label
                            }
                        })
                        addMidiTracksToProject(service, prefixedTracks, bpm)
                        allNewTracks.push(...prefixedTracks)
                    }
                }

                allMidiTracks.push(...allNewTracks)

                addMessage({
                    role: "assistant",
                    content: result.message,
                    midiTracks: allNewTracks
                })
            } else {
                // Chat mode: use GPT-5 via Replicate with MIDI context
                const thinkingEl: HTMLDivElement = <div className="ai-chat-msg assistant"><div className="ai-spinner" style={{display: "inline-block", marginRight: "8px"}}/>Thinking (GPT-5)...</div>
                messagesEl.appendChild(thinkingEl)
                messagesEl.scrollTop = messagesEl.scrollHeight

                const response = await chatAboutMusicGPT5(text, chatHistory, allMidiTracks)
                messagesEl.removeChild(thinkingEl)
                addMessage({role: "assistant", content: response})
            }
        } catch (e: any) {
            addMessage({role: "assistant", content: `Error: ${e.message}`})
        } finally {
            processing = false
        }
    }

    showEmpty()

    return (
        <div className="ai-content" style={{padding: "0", display: "flex", flexDirection: "column", height: "100%"}}>
            <div style={{flex: "1", overflow: "hidden", display: "flex", flexDirection: "column", padding: "12px 12px 0"}}>
                {messagesEl}
            </div>
            <div className="ai-chat-input-area" style={{padding: "8px 12px 12px"}}>
                <div className="ai-mode-switch">
                    {agentBtn}
                    {chatBtn}
                    {modeDesc}
                </div>
                <div className="ai-chat-input-row">
                    {inputEl}
                    <button className="ai-send-btn" onclick={doSend}>&#9654;</button>
                </div>
            </div>
        </div>
    )
}

// ──────────────────────── Sound AI ────────────────────────

const SoundAITab = ({service}: Construct) => {
    let prompt = ""
    let duration = 30
    let bpm = 120
    let generating = false

    const statusEl: HTMLDivElement = <div style={{display: "none"}}/>
    const textarea: HTMLTextAreaElement = (
        <textarea className="ai-textarea"
                  placeholder="E.g., upbeat 80s synthwave with driving bass and neon arpeggios..."
                  oninput={(e: Event) => prompt = (e.target as HTMLTextAreaElement).value}/>
    )
    const durationLabel: HTMLSpanElement = <span>{duration}s</span>
    const bpmInput: HTMLSpanElement = <span>{bpm}</span>
    const generateBtn: HTMLButtonElement = <button className="ai-generate-btn">Generate {duration}s Track</button>

    const slider: HTMLInputElement = (
        <input type="range" className="ai-slider" min="5" max="60" value={String(duration)}
               oninput={(e: Event) => {
                   duration = parseInt((e.target as HTMLInputElement).value)
                   durationLabel.textContent = `${duration}s`
                   generateBtn.textContent = `Generate ${duration}s Track`
               }}/>
    )

    const setDuration = (val: number) => {
        duration = val
        slider.value = String(val)
        durationLabel.textContent = val >= 60 ? `${Math.floor(val / 60)}m ${val % 60}s` : `${val}s`
        generateBtn.textContent = `Generate ${durationLabel.textContent} Track`
    }

    const setBpm = (val: number) => {
        bpm = val
        bpmInput.textContent = String(val)
    }

    const showStatus = (msg: string, type: "" | "success" | "error" = "") => {
        statusEl.style.display = "flex"
        statusEl.className = `ai-status ${type}`
        statusEl.innerHTML = type === "" ? `<div class="ai-spinner"></div>${msg}` : msg
    }

    const doGenerate = async () => {
        if (generating || !prompt.trim()) return
        if (totalRemaining() <= 0) { showCheckoutModal(); return }
        useCredit()
        generating = true
        generateBtn.disabled = true
        try {
            showStatus("Generating sound with ElevenLabs...")
              trackAnalyticsApiCall("/api/replicate/predictions", "replicate-soundgen")
              const audioUrl = await generateSoundAI(prompt, duration, bpm)
            showStatus("Downloading audio...")
            const audioBuffer = await fetchAudioAsArrayBuffer(audioUrl)
            await importAudioToTrack(service, `Sound: ${prompt.slice(0, 25)}`, audioBuffer)
            showStatus("Sound imported to track!", "success")
        } catch (e: any) {
            showStatus(`Error: ${e.message}`, "error")
        } finally {
            generating = false
            generateBtn.disabled = false
        }
    }

    generateBtn.onclick = doGenerate

    const setPrompt = (text: string) => {
        prompt = text
        textarea.value = text
    }

    return (
        <div className="ai-content">
            <div>
                <h3>Sound AI</h3>
                <p className="ai-subtitle">Generate a full studio-quality song as a single track using ElevenLabs Music.</p>
            </div>
            <div>
                <div className="ai-label">Describe your song</div>
                {textarea}
            </div>
            <div>
                <div className="ai-control-row">
                    <span className="ai-control-label">Duration</span>
                    <span className="ai-control-value">{durationLabel}</span>
                </div>
                {slider}
                <div style={{display: "flex", justifyContent: "space-between", fontSize: "9px", color: "#666", marginTop: "2px"}}>
                    <span>5s</span><span>5 min</span>
                </div>
                <div className="ai-preset-row" style={{marginTop: "6px"}}>
                    {[15, 30, 60, 120, 180].map(d =>
                        <button className={`ai-preset-btn${d === 30 ? " active" : ""}`}
                                onclick={() => setDuration(d)}>
                            {d >= 60 ? `${d / 60}m` : `${d}s`}
                        </button>
                    )}
                </div>
            </div>
            <div>
                <div className="ai-control-row">
                    <span className="ai-control-label">BPM</span>
                    <span style={{fontSize: "10px", color: "#666"}}>
                        Project: {service.hasProfile ? Math.round(service.project.timelineBoxAdapter.box.bpm.getValue()) : "—"}
                    </span>
                </div>
                <div className="ai-control-value" style={{marginBottom: "6px"}}>{bpmInput}</div>
                <div className="ai-preset-row">
                    {[80, 100, 120, 140].map(b =>
                        <button className={`ai-preset-btn${b === 120 ? " active" : ""}`}
                                onclick={() => setBpm(b)}>
                            {b}
                        </button>
                    )}
                    <button className="ai-preset-btn" onclick={() => {
                        if (service.hasProfile) {
                            setBpm(Math.round(service.project.timelineBoxAdapter.box.bpm.getValue()))
                        }
                    }}>Sync</button>
                </div>
            </div>
            <div className="ai-toggle-row">
                <div className="ai-toggle-text">
                    <h4>Instrumental only</h4>
                    <p>Disable to allow vocals/lyrics</p>
                </div>
                <label className="ai-toggle">
                    <input type="checkbox" checked={true}/>
                    <div className="ai-toggle-slider"/>
                </label>
            </div>
            {generateBtn}
            {statusEl}
            <div className="ai-ideas">
                <div className="ai-ideas-label">Ideas</div>
                {["Dark trap beat with heavy 808 and hi-hats", "EDM festival banger with supersaw drops",
                    "Drill beat with sliding bass and dark melodies", "Future bass with vocal chops and plucks",
                    "Phonk beat with cowbells and distorted 808", "House track with acid bass and arp synths"].map(idea =>
                    <button className="ai-idea-btn" onclick={() => setPrompt(idea)}>{idea}</button>
                )}
            </div>
        </div>
    )
}

// ──────────────────────── Library ────────────────────────

const LibraryTab = ({service}: Construct) => {
    let searchQuery = ""
    let selectedCategory = "All"
    const audioCache = new Map<string, string>()
    const loadingItems = new Set<string>()
    const itemElements = new Map<string, HTMLDivElement>()
    const listEl: HTMLDivElement = <div className="ai-library-list"/>

    const renderList = () => {
        listEl.innerHTML = ""
        itemElements.clear()
        const filtered = LIBRARY_SAMPLES.filter(sample => {
            if (selectedCategory !== "All" && sample.category !== selectedCategory) return false
            if (searchQuery && !sample.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
                !sample.description.toLowerCase().includes(searchQuery.toLowerCase())) return false
            return true
        })
        for (const sample of filtered) {
            const isLoading = loadingItems.has(sample.id)
            const addBtn: HTMLButtonElement = (
                <button className="ai-lib-add" onclick={(event: Event) => {
                    event.stopPropagation()
                    addSampleToTrack(sample)
                }}>{isLoading ? "..." : "+"}</button>
            )
            if (isLoading) addBtn.disabled = true
            const item: HTMLDivElement = (
                <div className="ai-library-item" onclick={() => addSampleToTrack(sample)}>
                    <div className="ai-lib-icon">🎵</div>
                    <div className="ai-lib-info">
                        <h5>{sample.name}</h5>
                        <p>{sample.description}{sample.bpm > 0 ? ` · ${sample.bpm} BPM` : ""} · {sample.duration}s</p>
                    </div>
                    {addBtn}
                </div>
            )
            if (isLoading) item.style.opacity = "0.6"
            itemElements.set(sample.id, item)
            listEl.appendChild(item)
        }
        if (filtered.length === 0) {
            listEl.appendChild(<div style={{padding: "20px", textAlign: "center", color: "#555", fontSize: "11px"}}>No samples found</div>)
        }
    }

    const addSampleToTrack = async (sample: LibrarySample) => {
        if (loadingItems.has(sample.id)) return
        if (totalRemaining() <= 0) { showCheckoutModal(); return }
        if (audioCache.has(sample.id)) {
            const cachedUrl = audioCache.get(sample.id)!
            const audioBuffer = await fetchAudioAsArrayBuffer(cachedUrl)
            await importAudioToTrack(service, sample.name, audioBuffer)
            return
        }
        useCredit()
        loadingItems.add(sample.id)
        const itemEl = itemElements.get(sample.id)
        const addBtn = itemEl?.querySelector(".ai-lib-add") as HTMLButtonElement | null
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = "..." }
        if (itemEl) itemEl.style.opacity = "0.6"
        try {
            trackAnalyticsApiCall("/api/replicate/predictions", "replicate-soundgen")
            const audioUrl = await generateSoundAI(sample.description, sample.duration, sample.bpm)
            audioCache.set(sample.id, audioUrl)
            const audioBuffer = await fetchAudioAsArrayBuffer(audioUrl)
            await importAudioToTrack(service, sample.name, audioBuffer)
        } catch (e: any) {
            console.error("Library generation error:", e)
        } finally {
            loadingItems.delete(sample.id)
            if (addBtn) { addBtn.disabled = false; addBtn.textContent = "+" }
            if (itemEl) itemEl.style.opacity = ""
        }
    }

    const catContainer: HTMLDivElement = <div className="ai-library-categories"/>
    for (const cat of LIBRARY_CATEGORIES) {
        const btn: HTMLButtonElement = (
            <button className={`ai-cat-btn${cat === "All" ? " active" : ""}`}
                    onclick={() => {
                        selectedCategory = cat
                        catContainer.querySelectorAll(".ai-cat-btn").forEach(b => b.classList.remove("active"))
                        btn.classList.add("active")
                        renderList()
                    }}>
                {cat}
            </button>
        )
        catContainer.appendChild(btn)
    }

    renderList()

    return (
        <div className="ai-content">
            <input className="ai-library-search" type="text" placeholder="Search sounds..."
                   oninput={(e: Event) => {
                       searchQuery = (e.target as HTMLInputElement).value
                       renderList()
                   }}/>
            {catContainer}
            {listEl}
        </div>
    )
}

